import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Edit3, History, Copy, Filter, ExternalLink, X, ChevronDown } from "lucide-react";

/**
 * V1.28 · KAI · Pestaña dentro del perfil del alumno.
 *
 * Muestra la biblioteca de plantillas WhatsApp filtrable por canal/categoría/tag,
 * un panel de vista previa con renderizado en vivo, edición inline (no modifica
 * la plantilla original) y los últimos 5 envíos con acceso al historial completo.
 */
export default function KaiStudentTab({ student }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel] = useState("whatsapp");
  const [category, setCategory] = useState("all");
  const [tag, setTag] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [previewText, setPreviewText] = useState("");
  const [previewSnapshot, setPreviewSnapshot] = useState({});
  const [unknownVars, setUnknownVars] = useState([]);
  const [editing, setEditing] = useState(false);
  const [customBody, setCustomBody] = useState("");
  const [messages, setMessages] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/kai/templates?channel=${channel}&active=true`);
      setTemplates(data.templates || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [channel]);

  const loadMessages = useCallback(async () => {
    if (!student?.id) return;
    try {
      const { data } = await api.get(`/kai/students/${student.id}/messages?limit=50`);
      setMessages(data.messages || []);
    } catch (err) {
      // silent
    }
  }, [student?.id]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => t.category && set.add(t.category));
    return Array.from(set).sort();
  }, [templates]);

  const tagOptions = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => (t.tags || []).forEach((tg) => set.add(tg)));
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => templates.filter((t) => {
    if (t.slug === "__legacy_migrated__") return false;
    if (category !== "all" && t.category !== category) return false;
    if (tag !== "all" && !(t.tags || []).includes(tag)) return false;
    return true;
  }), [templates, category, tag]);

  const selected = useMemo(() => filtered.find((t) => t.id === selectedId) || null, [filtered, selectedId]);

  // Preview live: cada vez que cambia la selección o el body editado.
  useEffect(() => {
    if (!selected || !student?.id) {
      setPreviewText("");
      setPreviewSnapshot({});
      setUnknownVars([]);
      return;
    }
    const payload = editing ? { body: customBody } : { template_id: selected.id };
    let cancelled = false;
    api.post(`/kai/students/${student.id}/preview`, payload).then(({ data }) => {
      if (cancelled) return;
      setPreviewText(data.rendered || "");
      setPreviewSnapshot(data.context_snapshot || {});
      setUnknownVars(data.unknown_variables || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selected, student?.id, editing, customBody]);

  const handleSelect = useCallback((t) => {
    setSelectedId(t.id);
    setEditing(false);
    setCustomBody(t.body || "");
  }, []);

  const handleStartEditing = useCallback(() => {
    if (!selected) return;
    setCustomBody(selected.body || "");
    setEditing(true);
  }, [selected]);

  const handleSend = useCallback(async () => {
    if (!selected || !student?.id) return;
    setSending(true);
    try {
      const payload = { template_id: selected.id };
      if (editing) payload.body = customBody;
      const { data } = await api.post(`/kai/students/${student.id}/send`, payload);
      if (data?.wa_url) {
        window.open(data.wa_url, "_blank", "noopener");
        toast.success("WhatsApp abierto en nueva pestaña y registrado en el historial");
      } else {
        toast.success("Mensaje registrado (sin teléfono válido para WhatsApp)");
      }
      loadMessages();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  }, [selected, student?.id, editing, customBody, loadMessages]);

  const handleCopy = useCallback(() => {
    if (!previewText) return;
    navigator.clipboard.writeText(previewText).then(() => {
      toast.success("Copiado al portapapeles");
    });
  }, [previewText]);

  const visibleMessages = historyOpen ? messages : messages.slice(0, 5);

  return (
    <div className="space-y-4" data-testid="kai-student-tab">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 pb-3">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-neutral-600">
          <Filter className="h-3.5 w-3.5" />
          Filtrar:
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="kai-filter-category"
          className="rounded-full border border-neutral-300 bg-white px-3 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="all">Todas las categorías</option>
          {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          data-testid="kai-filter-tag"
          className="rounded-full border border-neutral-300 bg-white px-3 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="all">Todas las etiquetas</option>
          {tagOptions.map((tg) => <option key={tg} value={tg}>{tg}</option>)}
        </select>
        <span className="ml-auto text-xs text-neutral-500">
          {filtered.length} plantilla{filtered.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        {/* Lista plantillas */}
        <div className="lg:col-span-2 space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="kai-template-list">
          {loading ? (
            <div className="text-sm text-neutral-500 py-4">Cargando plantillas…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-neutral-500 py-4">No hay plantillas activas para este filtro.</div>
          ) : filtered.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => handleSelect(t)}
              data-testid={`kai-template-${t.slug}`}
              className={
                "w-full text-left rounded-lg border px-3 py-2 transition-all " +
                (selectedId === t.id
                  ? "border-orange-500 bg-orange-50 ring-1 ring-orange-200"
                  : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50")
              }
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{t.title}</div>
                  <div className="text-[11px] text-neutral-500 truncate">{t.category} · v{t.version}</div>
                </div>
                {t.is_system && (
                  <span className="text-[9px] font-semibold text-neutral-500 bg-neutral-100 rounded-full px-1.5 py-0.5">SYS</span>
                )}
              </div>
              {(t.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(t.tags || []).slice(0, 4).map((tg) => (
                    <span key={tg} className="text-[10px] rounded-full bg-neutral-100 text-neutral-700 px-1.5 py-0.5">{tg}</span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Preview + Envío */}
        <div className="lg:col-span-3 space-y-3" data-testid="kai-preview-panel">
          {!selected ? (
            <div className="rounded-lg border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">
              ← Selecciona una plantilla para ver la vista previa
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                <div className="flex items-center justify-between bg-neutral-50 border-b border-neutral-200 px-3 py-2">
                  <div className="text-xs font-semibold text-neutral-700 truncate">
                    Vista previa · <span className="text-neutral-500">{selected.title}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {!editing && (
                      <button
                        type="button"
                        onClick={handleStartEditing}
                        data-testid="kai-edit-inline"
                        className="text-[11px] font-semibold text-orange-700 hover:text-orange-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-orange-50"
                        title="Editar sólo este envío (no modifica la plantilla)"
                      >
                        <Edit3 className="h-3 w-3" /> Editar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleCopy}
                      data-testid="kai-copy-preview"
                      className="text-[11px] font-semibold text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-neutral-100"
                    >
                      <Copy className="h-3 w-3" /> Copiar
                    </button>
                  </div>
                </div>
                {editing ? (
                  <div>
                    <textarea
                      value={customBody}
                      onChange={(e) => setCustomBody(e.target.value)}
                      data-testid="kai-inline-editor"
                      className="w-full min-h-[130px] p-3 text-sm font-mono focus:outline-none"
                      placeholder="Escribe el mensaje. Puedes usar variables tipo {{nombre}}, {{documentos_pendientes}}…"
                    />
                    <div className="flex items-center justify-between px-3 py-2 border-t border-neutral-200 bg-neutral-50">
                      <span className="text-[10px] text-neutral-500">La edición se aplica solo a este envío.</span>
                      <button
                        type="button"
                        onClick={() => { setEditing(false); setCustomBody(selected.body || ""); }}
                        className="text-[11px] font-semibold text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1"
                        data-testid="kai-cancel-edit"
                      >
                        <X className="h-3 w-3" /> Cancelar edición
                      </button>
                    </div>
                  </div>
                ) : null}
                <div className="p-3 text-sm whitespace-pre-wrap text-neutral-900" data-testid="kai-preview-rendered">
                  {previewText || <span className="text-neutral-400 italic">(sin contenido)</span>}
                </div>
                {unknownVars.length > 0 && (
                  <div className="border-t border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-800">
                    ⚠ Variables desconocidas: {unknownVars.map((v) => `{{${v}}}`).join(", ")}
                  </div>
                )}
                {Object.keys(previewSnapshot).length > 0 && (
                  <details className="border-t border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
                    <summary className="cursor-pointer font-semibold">Ver variables usadas ({Object.keys(previewSnapshot).length})</summary>
                    <div className="mt-2 space-y-0.5 font-mono">
                      {Object.entries(previewSnapshot).map(([k, v]) => (
                        <div key={k}><span className="text-neutral-500">{`{{${k}}}`}</span> → <span>{String(v) || <em className="text-neutral-400">(vacío)</em>}</span></div>
                      ))}
                    </div>
                  </details>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || !student?.phone}
                  data-testid="kai-send-whatsapp"
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  title={student?.phone ? "" : "Este alumno no tiene teléfono"}
                >
                  <Send className="h-4 w-4" />
                  {sending ? "Enviando…" : "Enviar WhatsApp"}
                </button>
                {!student?.phone && (
                  <span className="text-[11px] text-red-700">Sin teléfono registrado</span>
                )}
                {editing && (
                  <span className="text-[11px] text-orange-700 font-semibold">Modo edición: se enviará el texto editado.</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Historial */}
      <div className="border-t border-neutral-200 pt-3" data-testid="kai-history">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
            <History className="h-4 w-4" />
            Historial de envíos {messages.length > 0 && <span className="text-xs text-neutral-500 font-normal">({messages.length})</span>}
          </div>
          {messages.length > 5 && (
            <button
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              data-testid="kai-history-toggle"
              className="text-xs font-semibold text-orange-700 hover:text-orange-900 inline-flex items-center gap-1"
            >
              <ChevronDown className={"h-3 w-3 transition-transform " + (historyOpen ? "rotate-180" : "")} />
              {historyOpen ? "Ocultar" : "Ver historial completo"}
            </button>
          )}
        </div>
        {messages.length === 0 ? (
          <div className="text-xs text-neutral-500 py-3 italic">Todavía no se ha enviado ningún mensaje a este alumno.</div>
        ) : (
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
            {visibleMessages.map((m) => (
              <div key={m.id} className="rounded-md border border-neutral-200 bg-white p-2" data-testid={`kai-message-${m.id}`}>
                <div className="flex items-center justify-between gap-2 text-[11px] text-neutral-500 mb-1">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-semibold text-neutral-700 truncate">
                      {new Date(m.sent_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span>·</span>
                    <span className="uppercase">{m.channel}</span>
                    <span>·</span>
                    <span className="truncate">{m.template_slug} v{m.template_version}</span>
                    {m.was_edited && (
                      <span className="text-orange-700 font-semibold">· editado</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-emerald-700 font-semibold">✓ {m.status}</span>
                    {m.wa_url && (
                      <a
                        href={m.wa_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-neutral-500 hover:text-neutral-900"
                        title="Reabrir en WhatsApp"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="text-xs text-neutral-800 whitespace-pre-wrap line-clamp-3">
                  {m.rendered_body}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
